import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { CinematicVideoPlayer } from '@/components/video/CinematicVideoPlayer';
import { VideoPreloader } from '@/components/video/VideoPreloader';
import { PhotoGallery } from '@/components/reveal/PhotoGallery';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { motion } from 'framer-motion';
import { Heart, ArrowLeft, Share2, Printer, Download, Mail, Volume2, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import type { Announcement, AnnouncementMetadata } from '@/types/announcement';
import type { FoundingMember } from '@/types/founder';
import { renderTemplateLayout } from '@/components/templates/TemplateLayouts';
import { PremiumAnnouncementReveal } from '@/components/reveal/PremiumAnnouncementReveal';
import { toast } from 'sonner';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { useAuth } from '@/hooks/useAuth';
import { useUserCredits } from '@/hooks/useUserCredits';
import { RELATIONSHIP_OPTIONS } from '@/types/announcement';
import { generatePrintHtml } from '@/lib/printUtils';
import { ShareAnnouncementModal } from '@/components/sharing/ShareAnnouncementModal';
import { FounderIndicator } from '@/components/founder/FounderIndicator';
import { RecipientActions } from '@/components/recipient/RecipientActions';
import { ReflectionNamePrompt } from '@/components/recipient/ReflectionNamePrompt';
import { LivingTreeConnection } from '@/components/recipient/LivingTreeConnection';
import { RecipientFooterCTA } from '@/components/recipient/RecipientFooterCTA';
import { RecipientViralCTA } from '@/components/recipient/RecipientViralCTA';
import { AnonymousReplyThread } from '@/components/thread/AnonymousReplyThread';
import { JacKeyPostReadBubble } from '@/components/recipient/JacKeyPostReadBubble';
import { RecipientShareSection } from '@/components/reveal/RecipientShareSection';


import { AnnouncementWatermark } from '@/components/watermark/AnnouncementWatermark';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { ListenButton } from '@/components/announcement/ListenButton';

const viewCopy = {
  en: { notFound: 'Announcement Not Found', notFoundDesc: 'This announcement may have been removed or the link is incorrect.', goHome: 'Go Home', dashboard: 'Dashboard', print: 'Print', share: 'Share', writtenWith: 'Written with', tagline: 'A private space for saying what matters.', ctaTitle: 'Want to send one too?', ctaCta: 'Write yours on Say It NOW', ctaSub: 'No account needed to read. Your words stay yours.', crushHeader: 'Someone Has Something to Tell You 💌', crushSentBy: 'Sent by', crushAnon: 'Anonymous', replyNow: 'Reply Now', nowYourTurn: 'Now It\'s Your Turn', nowYourTurnDesc: 'Send your own heartfelt message' },
  es: { notFound: 'Anuncio No Encontrado', notFoundDesc: 'Este anuncio puede haber sido eliminado o el enlace es incorrecto.', goHome: 'Ir al Inicio', dashboard: 'Panel', print: 'Imprimir', share: 'Compartir', writtenWith: 'Escrito con', tagline: 'Un espacio privado para decir lo que importa.', ctaTitle: '¿Quieres enviar uno también?', ctaCta: 'Escribe el tuyo en Say It NOW', ctaSub: 'No necesitas cuenta para leer. Tus palabras son tuyas.', crushHeader: 'Alguien tiene algo que decirte 💌', crushSentBy: 'Enviado por', crushAnon: 'Anónimo', replyNow: 'Responder Ahora', nowYourTurn: 'Ahora es tu turno', nowYourTurnDesc: 'Envía tu propio mensaje sincero' },
  fr: { notFound: 'Annonce Introuvable', notFoundDesc: 'Cette annonce a peut-être été supprimée ou le lien est incorrect.', goHome: 'Retour à l\'accueil', dashboard: 'Tableau de bord', print: 'Imprimer', share: 'Partager', writtenWith: 'Écrit avec', tagline: 'Un espace privé pour dire ce qui compte.', ctaTitle: 'Envie d\'en envoyer un aussi ?', ctaCta: 'Écrivez le vôtre sur Say It NOW', ctaSub: 'Pas besoin de compte pour lire. Vos mots restent les vôtres.', crushHeader: 'Quelqu\'un a quelque chose à vous dire 💌', crushSentBy: 'Envoyé par', crushAnon: 'Anonyme', replyNow: 'Répondre maintenant', nowYourTurn: 'À votre tour', nowYourTurnDesc: 'Envoyez votre propre message sincère' },
  pt: { notFound: 'Anúncio Não Encontrado', notFoundDesc: 'Este anúncio pode ter sido removido ou o link está incorreto.', goHome: 'Ir para o Início', dashboard: 'Painel', print: 'Imprimir', share: 'Compartilhar', writtenWith: 'Escrito com', tagline: 'Um espaço privado para dizer o que importa.', ctaTitle: 'Quer enviar um também?', ctaCta: 'Escreva o seu no Say It NOW', ctaSub: 'Não precisa de conta para ler. Suas palavras são suas.', crushHeader: 'Alguém tem algo para te dizer 💌', crushSentBy: 'Enviado por', crushAnon: 'Anônimo', replyNow: 'Responder Agora', nowYourTurn: 'Agora é a sua vez', nowYourTurnDesc: 'Envie sua própria mensagem sincera' },
  ar: { notFound: 'الإعلان غير موجود', notFoundDesc: 'ربما تمت إزالة هذا الإعلان أو الرابط غير صحيح.', goHome: 'الرئيسية', dashboard: 'لوحة التحكم', print: 'طباعة', share: 'مشاركة', writtenWith: 'كُتب بواسطة', tagline: 'مساحة خاصة لقول ما يهم.', ctaTitle: 'هل تريد إرسال واحدة أيضاً؟', ctaCta: 'اكتب رسالتك على Say It NOW', ctaSub: 'لا حاجة لحساب للقراءة. كلماتك ملكك.', crushHeader: 'شخص ما لديه شيء يريد إخبارك به 💌', crushSentBy: 'أُرسل من', crushAnon: 'مجهول', replyNow: 'رد الآن', nowYourTurn: 'الآن دورك', nowYourTurnDesc: 'أرسل رسالتك الصادقة' },
  ja: { notFound: 'アナウンスが見つかりません', notFoundDesc: 'このアナウンスは削除されたか、リンクが正しくない可能性があります。', goHome: 'ホームへ', dashboard: 'ダッシュボード', print: '印刷', share: '共有', writtenWith: '作成ツール', tagline: '大切なことを伝えるプライベートな空間。', ctaTitle: 'あなたも送ってみませんか？', ctaCta: 'Say It NOWであなたのメッセージを書く', ctaSub: '読むのにアカウントは不要です。あなたの言葉はあなたのものです。', crushHeader: '誰かがあなたに伝えたいことがあります 💌', crushSentBy: '送信者', crushAnon: '匿名', replyNow: '今すぐ返信', nowYourTurn: 'あなたの番です', nowYourTurnDesc: '心のこもったメッセージを送りましょう' },
  ko: { notFound: '공지를 찾을 수 없습니다', notFoundDesc: '이 공지는 삭제되었거나 링크가 올바르지 않을 수 있습니다.', goHome: '홈으로', dashboard: '대시보드', print: '인쇄', share: '공유', writtenWith: '작성 도구', tagline: '중요한 것을 말하기 위한 프라이빗 공간.', ctaTitle: '당신도 하나 보내시겠어요?', ctaCta: 'Say It NOW에서 나만의 메시지를 작성하세요', ctaSub: '읽기에 계정이 필요 없습니다. 당신의 말은 당신의 것입니다.', crushHeader: '누군가 당신에게 전하고 싶은 말이 있습니다 💌', crushSentBy: '보낸 사람', crushAnon: '익명', replyNow: '지금 답장하기', nowYourTurn: '이제 당신 차례입니다', nowYourTurnDesc: '진심 어린 메시지를 보내세요' },
  id: { notFound: 'Pengumuman Tidak Ditemukan', notFoundDesc: 'Pengumuman ini mungkin telah dihapus atau tautannya salah.', goHome: 'Ke Beranda', dashboard: 'Dasbor', print: 'Cetak', share: 'Bagikan', writtenWith: 'Ditulis dengan', tagline: 'Ruang pribadi untuk mengungkapkan yang penting.', ctaTitle: 'Ingin mengirim satu juga?', ctaCta: 'Tulis milikmu di Say It NOW', ctaSub: 'Tidak perlu akun untuk membaca. Kata-katamu milikmu.', crushHeader: 'Seseorang ingin menyampaikan sesuatu kepadamu 💌', crushSentBy: 'Dikirim oleh', crushAnon: 'Anonim', replyNow: 'Balas Sekarang', nowYourTurn: 'Sekarang Giliranmu', nowYourTurnDesc: 'Kirim pesan tulusmu sendiri' },
};

function resolveSenderLabel(announcement: Announcement): string | undefined {
  const courageMode = (announcement as any).courage_mode;
  const revealedAt = (announcement as any).revealed_at;
  
  if (courageMode === 'reveal_when_ready') {
    if (!revealedAt) return undefined;
  } else if (announcement.is_anonymous) {
    return undefined;
  }
  
  const meta = (announcement as any).metadata as AnnouncementMetadata | null;
  if (meta?.senderDisplayName) return meta.senderDisplayName;
  if (meta?.senderNickname) return meta.senderNickname;
  if (announcement.sender_display_name) return announcement.sender_display_name;
  return undefined;
}

function cleanOGDescription(message: string): string {
  const clean = message.replace(/\n+/g, ' ').replace(/\s+/g, ' ').trim();
  if (clean.length <= 160) return clean;
  const truncated = clean.slice(0, 155);
  const lastSpace = truncated.lastIndexOf(' ');
  const result = lastSpace > 100 ? truncated.slice(0, lastSpace) : truncated;
  return result + '…';
}

function isExternalArrival(): boolean {
  const params = new URLSearchParams(window.location.search);
  if (params.get('preview') === 'true') return true;
  if (!document.referrer) return true;
  try {
    const ref = new URL(document.referrer);
    if (ref.origin !== window.location.origin) return true;
  } catch {
    return true;
  }
  return false;
}

export default function AnnouncementView() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { credits } = useUserCredits();
  const { language } = useLanguage();
  const vc = lc(viewCopy, language);
  const { slug } = useParams<{ slug: string }>();
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [isFounderAnnouncement, setIsFounderAnnouncement] = useState(false);
  const [isViewerOwner, setIsViewerOwner] = useState(false);
  const [senderReferralCode, setSenderReferralCode] = useState<string | null>(null);
  
  const highestBundle = credits?.highest_bundle_purchased || null;
  const hasConnectionAccess = highestBundle === 'connection' || highestBundle === 'legacy';
  const hasLegacyAccess = highestBundle === 'legacy';

  const metaTags = useMemo(() => {
    if (!announcement) return { title: 'Say It NOW — While They\'re Still Here', description: 'A private space for saying what matters.', meta: [] };
    const title = 'A message for you | Say It NOW';
    const description = cleanOGDescription(announcement.message);
    const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
    const ogImageUrl = typeof window !== 'undefined' ? `${window.location.origin}/og-share.jpg` : '/og-share.jpg';
    return {
      title,
      description,
      meta: [
        { property: 'og:type', content: 'article' },
        { property: 'og:url', content: currentUrl },
        { property: 'og:site_name', content: 'Say It NOW — While They\'re Still Here' },
        { property: 'og:image', content: ogImageUrl },
        { property: 'og:image:width', content: '1216' },
        { property: 'og:image:height', content: '640' },
        { property: 'og:image:alt', content: 'A message for you — Say It NOW' },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:site', content: '@SayItNOW' },
        { name: 'twitter:image', content: ogImageUrl },
      ],
    };
  }, [announcement]);

  useDocumentHead(metaTags);

  useEffect(() => {
    if (!slug) {
      setNotFound(true);
      setIsLoading(false);
      return;
    }

    const fetchAnnouncement = async () => {
      const { data: rows, error } = await (supabase as any).rpc('get_announcement_by_slug', {
        p_slug: slug,
      });
      const data = Array.isArray(rows) ? rows[0] : rows;

      if (error) {
        console.error('Error fetching announcement:', error);
        setNotFound(true);
      } else if (!data) {
        setNotFound(true);
      } else {
        const announcementData = data as unknown as Announcement;
        setAnnouncement(announcementData);

        // Check ownership: if user is logged in, check if they own this announcement
        if (user) {
          const { data: ownedData } = await supabase
            .from('announcements')
            .select('id')
            .eq('public_slug', slug)
            .eq('user_id', user.id)
            .maybeSingle();
          setIsViewerOwner(!!ownedData);
        }

        try {
          const { data: isFounder } = await supabase.rpc('is_founder_announcement' as any, {
            p_public_slug: slug,
          });
          if (isFounder) setIsFounderAnnouncement(true);
        } catch {
          // Non-critical
        }

        // Fetch sender's referral code for viral attribution
        try {
          const userId = (data as any).user_id || announcementData.user_id;
          if (userId) {
            const { data: profileData } = await supabase
              .from('profiles')
              .select('referral_code')
              .eq('user_id', userId)
              .maybeSingle();
            if (profileData?.referral_code) {
              setSenderReferralCode(profileData.referral_code);
              // Store for attribution if recipient signs up
              const existing = localStorage.getItem('sayitnow_ref');
              if (!existing) {
                localStorage.setItem('sayitnow_ref', profileData.referral_code);
              }
            }
          }
        } catch {
          // Non-critical
        }
      }
      setIsLoading(false);
    };

    fetchAnnouncement();
  }, [slug, user]);
  
  const handlePrint = () => {
    if (!hasConnectionAccess) {
      toast.error('Print is available for Connection and Legacy plans');
      return;
    }
    if (!announcement) return;
    const relationship = RELATIONSHIP_OPTIONS.find(r => r.value === announcement.relationship);
    const printContent = generatePrintHtml(announcement, relationship?.label, hasLegacyAccess);
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.onload = () => { printWindow.print(); };
    }
  };
  
  const handleDownloadPdf = () => {
    if (!hasConnectionAccess) {
      toast.error('PDF download is available for Connection and Legacy plans');
      return;
    }
    if (!announcement) return;
    const relationship = RELATIONSHIP_OPTIONS.find(r => r.value === announcement.relationship);
    const printContent = generatePrintHtml(announcement, relationship?.label, hasLegacyAccess);
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      toast.success('Print dialog opened — choose "Save as PDF" to download');
      printWindow.onload = () => { printWindow.print(); };
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 to-secondary/5">
        <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 1.5, repeat: Infinity }}>
          <Heart className="h-12 w-12 text-primary" />
        </motion.div>
      </div>
    );
  }

  if (notFound || !announcement) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-muted to-background p-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
          <Heart className="h-16 w-16 text-muted-foreground/50 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-foreground mb-2">{vc.notFound}</h1>
          <p className="text-muted-foreground mb-8">{vc.notFoundDesc}</p>
          <Link to="/">
            <Button variant="outline" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              {vc.goHome}
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  const publishedDate = announcement.published_at 
    ? new Date(announcement.published_at).toLocaleDateString(language === 'ar' ? 'ar-SA' : language, { year: 'numeric', month: 'long', day: 'numeric' })
    : new Date(announcement.created_at).toLocaleDateString(language === 'ar' ? 'ar-SA' : language, { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <MobileAnnouncementLayout
      user={user}
      isViewerOwner={isViewerOwner}
      announcement={announcement}
      slug={slug!}
      isFounderAnnouncement={isFounderAnnouncement}
      senderReferralCode={senderReferralCode}
      hasConnectionAccess={hasConnectionAccess}
      hasLegacyAccess={hasLegacyAccess}
      publishedDate={publishedDate}
      shareDialogOpen={shareDialogOpen}
      setShareDialogOpen={setShareDialogOpen}
      handlePrint={handlePrint}
      handleDownloadPdf={handleDownloadPdf}
      navigate={navigate}
      language={language}
      vc={vc}
    />
  );
}

function MobileAnnouncementLayout({
  user,
  isViewerOwner,
  announcement,
  slug,
  isFounderAnnouncement,
  senderReferralCode,
  hasConnectionAccess,
  hasLegacyAccess,
  publishedDate,
  shareDialogOpen,
  setShareDialogOpen,
  handlePrint,
  handleDownloadPdf,
  navigate,
  language,
  vc,
}: any) {
  const isMobile = useIsMobile();
  const [revealDone, setRevealDone] = useState(false);
  const replySectionRef = useRef<HTMLDivElement>(null);

  const showWatermark = (announcement as any).has_watermark === true;

  // Determine if this is a recipient view (not the owner)
  const isRecipientView = !isViewerOwner;

  const scrollToReply = useCallback(() => {
    replySectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <div className="relative">
      <AnnouncementWatermark visible={showWatermark} />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="container flex items-center justify-between h-14 px-4">
          {isViewerOwner ? (
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              {vc.dashboard}
            </Button>
          ) : (
            <Link to="/" className="flex items-center gap-2">
              <Heart className="h-5 w-5 text-primary" />
              <span className="font-semibold text-foreground">Say It NOW</span>
            </Link>
          )}
          <div className="flex items-center gap-1">
            {isViewerOwner && hasConnectionAccess && (
              <>
                <Button variant="ghost" size="sm" onClick={handlePrint} className="gap-2" title="Print this announcement">
                  <Printer className="h-4 w-4" />
                  <span className="hidden sm:inline">{vc.print}</span>
                </Button>
                <Button variant="ghost" size="sm" onClick={handleDownloadPdf} className="gap-2" title="Save as PDF">
                  <Download className="h-4 w-4" />
                  <span className="hidden sm:inline">PDF</span>
                </Button>
              </>
            )}
            <Button variant="ghost" size="sm" onClick={() => setShareDialogOpen(true)} className="gap-2">
              <Share2 className="h-4 w-4" />
              <span className="hidden sm:inline">{vc.share}</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <>
        {announcement.relationship === 'crush' && isRecipientView && (
          <CrushRecipientHeader announcement={announcement} language={language} vc={vc} />
        )}
        <div className="pt-14">
          <VideoPreloader src={(announcement as any).metadata?.videoUrl} photoUrls={announcement.photo_urls} />
          <PremiumAnnouncementReveal
            announcementId={(announcement as any).id}
            animationTheme={(announcement as any).animation_theme || 'elegant_floral'}
            recipientName={announcement.recipient_name}
            relationship={announcement.relationship}
            message={announcement.message}
            template={announcement.template}
            photoUrls={announcement.photo_urls || undefined}
            publishedDate={publishedDate}
            skipReveal={isViewerOwner && !isExternalArrival()}
            senderLabel={resolveSenderLabel(announcement)}
            metadata={(announcement as any).metadata as { chibiUrl?: string } | undefined}
            onRevealComplete={() => setRevealDone(true)}
            onShareRequest={() => setShareDialogOpen(true)}
            hideReplay={isViewerOwner}
          >
            {/* Attached photos gallery — tap to view full-screen + download */}
            <PhotoGallery
              photoUrls={announcement.photo_urls}
              transition={((announcement as any).metadata?.mediaTransition) ?? null}
              animationTheme={(announcement as any).animation_theme ?? null}
            />

            {/* Video message section — cinematic player */}
            {(() => {
              const meta = (announcement as any).metadata as Record<string, any> | null;
              const videoUrl = meta?.videoUrl;
              if (!videoUrl) return null;
              console.log('[AnnouncementView] rendering video attachment', { videoUrl });
              return <CinematicVideoPlayer src={videoUrl} poster={announcement.photo_urls?.[0]} />;
            })()}

            <div className="flex justify-center py-5">
              <ListenButton message={announcement.message} announcementSlug={slug} />
            </div>

            {/* Recipient-side polished share section — appears after reveal + attachments */}
            {isRecipientView && slug && (
              <RecipientShareSection
                publicSlug={slug}
                recipientName={announcement.recipient_name}
                relationship={announcement.relationship}
                template={announcement.template}
                senderName={resolveSenderLabel(announcement)}
                senderReferralCode={senderReferralCode}
                photoUrls={announcement.photo_urls}
                videoUrl={(announcement as any).metadata?.videoUrl ?? null}
                source="recipient_reveal_final"
              />
            )}
          </PremiumAnnouncementReveal>
        </div>

        {/* JacKey post-read bubble — single encouragement CTA */}
        {isRecipientView && announcement.id && revealDone && (
          <JacKeyPostReadBubble
            onReply={scrollToReply}
            source={isMobile ? "announcement_view_mobile" : "announcement_view"}
          />
        )}

        {/* Recipient actions (Save + Report only) — after reveal */}
        {isRecipientView && announcement.id && slug && revealDone && (
          <RecipientActions announcementId={announcement.id} publicSlug={slug} />
        )}

        {/* Reply thread — recipient view (single reply CTA) — after reveal */}
        {isRecipientView && announcement.id && revealDone && (
          <div id="reply-section" ref={replySectionRef} className="py-6 pb-20 flex justify-center px-4">
            <AnonymousReplyThread announcementId={announcement.id} isOwner={false} />
          </div>
        )}

        {/* Reply thread — owner view */}
        {isViewerOwner && announcement.id && (
          <div className="py-6 pb-20 flex justify-center px-4">
            <AnonymousReplyThread announcementId={announcement.id} isOwner />
          </div>
        )}

        {/* Recipient engagement CTAs — after reveal */}
        {isRecipientView && revealDone && (() => {
          const metadata = (announcement as any).metadata as AnnouncementMetadata | null;
          const senderName = metadata?.senderDisplayName || metadata?.senderNickname || (announcement as any).sender_display_name || undefined;
          return (
            <>
              <ReflectionNamePrompt source={isMobile ? "announcement_view_mobile" : "announcement_view"} />
              <LivingTreeConnection senderName={senderName} />
              <RecipientViralCTA
                source={isMobile ? "announcement_view_mobile" : "announcement_view"}
                senderName={senderName}
                senderReferralCode={senderReferralCode}
              />
            </>
          );
        })()}
      </>

      {/* Attribution footer */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-background/90 backdrop-blur-sm border-t border-border/50 py-2 px-4">
        <div className="container max-w-2xl mx-auto text-center">
          {isFounderAnnouncement && (
            <div className="mb-1">
              <FounderIndicator />
            </div>
          )}
          <p className="text-[11px] text-muted-foreground/70">
            {vc.writtenWith}{' '}
            <a href="/" target="_blank" rel="noopener noreferrer" className="text-muted-foreground/70 hover:text-muted-foreground transition-colors hover:underline">
              Say It NOW
            </a>
            {' • '}
            <span className="text-muted-foreground/50">{vc.tagline}</span>
          </p>
        </div>
      </div>

      {/* Share Dialog */}
      {slug && (
        <ShareAnnouncementModal
          open={shareDialogOpen}
          onOpenChange={setShareDialogOpen}
          announcementId={announcement.id}
          publicSlug={slug}
          recipientName={announcement.recipient_name}
          message={announcement.message}
          template={announcement.template}
          relationship={announcement.relationship}
          source={isViewerOwner ? 'announcement_view' : 'recipient_page'}
        />
      )}
    </div>
  );
}

function CrushRecipientHeader({ announcement, language, vc }: { announcement: Announcement; language: string; vc: any }) {
  const metadata = (announcement as any).metadata as AnnouncementMetadata | null;
  const mode = metadata?.senderDisplayMode;
  
  const senderLabel = (() => {
    if (!mode || mode === 'anonymous') return vc.crushAnon;
    if (mode === 'confession') return '🔒 ' + vc.crushAnon;
    if (mode === 'nickname') return metadata?.senderNickname || vc.crushAnon;
    return vc.crushSentBy;
  })();

  return (
    <div className="pt-14">
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-b from-primary/5 to-transparent py-8 px-4 text-center"
      >
        <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-primary/10 text-primary mb-3">
          <Mail className="h-7 w-7" />
        </div>
        <h2 className="text-xl font-bold text-foreground mb-1">{vc.crushHeader}</h2>
        <p className="text-sm text-muted-foreground">
          {vc.crushSentBy}: <span className="font-medium text-foreground">{senderLabel}</span>
        </p>
      </motion.div>
    </div>
  );
}
